import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { CampdeskLogo } from '@/components/svg/CampdeskLogo.tsx';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import { BackendApiError, KnownBackendApiError } from '@/lib/api/exception.ts';
import { EmailSettingStatus } from '@/features/dashboard/emailSender/types/EmailSettingStatus.ts';
import {
  emailSettingStatusTones,
  getEmailSettingStatusLabel,
} from '@/features/dashboard/emailSender/types/EmailSettingStatus.utils.ts';
import useGetSettingApprovalPreview from '@/features/public/settingApproval/hooks/useGetSettingApprovalPreview.ts';
import useApproveSetting from '@/features/public/settingApproval/hooks/useApproveSetting.ts';
import SettingApprovalPreviewSkeleton from '@/features/public/settingApproval/components/SettingApprovalPreviewSkeleton.tsx';
import { getApprovalSettingNameLabel } from '@/features/public/settingApproval/types/ApprovalSettingName.utils.ts';
import { settingApprovalRoute } from '@/features/public/settingApproval/routes/settingApprovalRoute.ts';
import { SettingApprovalPreviewDto } from '@/features/public/settingApproval/types/SettingApprovalDto.ts';

const getErrorMessage = (error: Error): string => {
  if (error instanceof KnownBackendApiError) {
    return error.message;
  }
  if (error instanceof BackendApiError && error.statusCode === 403) {
    return 'Link jest nieprawidłowy lub wygasł. Poproś o nowy link zatwierdzenia.';
  }
  return error.message;
};

const ErrorState = ({ message }: { message: string }) => (
  <div className="flex w-full flex-col items-center gap-3 text-center">
    <XCircle className="size-10 text-error-300" />
    <p className="text-sm text-neutral-600">{message}</p>
  </div>
);

const SuccessState = ({
  settingName,
  value,
}: {
  settingName: string;
  value: string;
}) => (
  <div className="flex w-full flex-col items-center gap-3 text-center">
    <CheckCircle2 className="size-10 text-accent-400" />
    <p className="text-sm text-neutral-600">
      {getApprovalSettingNameLabel(settingName)} został zatwierdzony dla
      wartości <span className="font-medium text-base-text">{value}</span>.
    </p>
  </div>
);

const PreviewDetails = ({
  preview,
}: {
  preview: SettingApprovalPreviewDto;
}) => (
  <InfoCardList className="w-full">
    <InfoCardRow layout="columns" label="Ustawienie">
      {getApprovalSettingNameLabel(preview.settingName)}
    </InfoCardRow>
    <InfoCardRow layout="columns" label="Wartość">
      {preview.value}
    </InfoCardRow>
    <InfoCardRow layout="columns" label="Status">
      {preview.status ? (
        <StatusDot
          tone={emailSettingStatusTones[preview.status]}
          label={getEmailSettingStatusLabel(preview.status)}
        />
      ) : (
        <span className="text-neutral-500">Brak zgłoszenia</span>
      )}
    </InfoCardRow>
  </InfoCardList>
);

const SettingApprovalPage = () => {
  const { token } = settingApprovalRoute.useSearch();
  const previewQuery = useGetSettingApprovalPreview(token);
  const approveMutation = useApproveSetting();

  const renderContent = () => {
    if (!token) {
      return (
        <ErrorState message="Brak tokenu w adresie URL. Link jest niekompletny — użyj pełnego linku z powiadomienia." />
      );
    }

    if (previewQuery.isLoading) {
      return <SettingApprovalPreviewSkeleton />;
    }

    if (previewQuery.isError) {
      return <ErrorState message={getErrorMessage(previewQuery.error)} />;
    }

    const preview = previewQuery.data;
    if (!preview) {
      return null;
    }

    if (approveMutation.data) {
      return (
        <SuccessState
          settingName={approveMutation.data.settingName}
          value={approveMutation.data.value}
        />
      );
    }

    if (preview.status === null) {
      return (
        <div className="flex w-full flex-col gap-6">
          <PreviewDetails preview={preview} />
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <Info className="size-8 text-neutral-400" />
            <p className="text-sm text-neutral-600">
              Brak aktywnego zgłoszenia dla tego ustawienia — nie ma nic do
              zatwierdzenia.
            </p>
          </div>
        </div>
      );
    }

    if (preview.status === EmailSettingStatus.APPROVED) {
      return (
        <div className="flex w-full flex-col gap-6">
          <PreviewDetails preview={preview} />
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <CheckCircle2 className="size-8 text-accent-400" />
            <p className="text-sm text-neutral-600">
              To zgłoszenie zostało już zatwierdzone.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex w-full flex-col gap-6">
        <PreviewDetails preview={preview} />
        {!preview.valueUpToDate && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <AlertTriangle className="size-8 text-error-300" />
            <p className="text-sm text-neutral-600">
              Organizator zmienił wartość po wygenerowaniu tego linku — link
              jest nieaktualny i zatwierdzenie zostanie odrzucone. Nowy link
              pojawi się na Chacie.
            </p>
          </div>
        )}
        {approveMutation.error && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <XCircle className="size-8 text-error-300" />
            <p className="text-sm text-error-300">
              {getErrorMessage(approveMutation.error)}
            </p>
          </div>
        )}
        <ActionButton
          tone="accent"
          size="block"
          onClick={() => approveMutation.mutate(token)}
          isLoading={approveMutation.isPending}
          disabled={!preview.valueUpToDate}
        >
          Zatwierdź ustawienie
        </ActionButton>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-basic-white p-10 max-md:p-8 max-sm:p-6">
      <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
        <CampdeskLogo />
        <AuthFormContainer title="Zatwierdzenie ustawienia nadawcy">
          {renderContent()}
        </AuthFormContainer>
      </div>
    </div>
  );
};

export default SettingApprovalPage;
